<template>
  <overlay
    id="size-guide-drawer"
    ref="sizeGuideDrawer"
    key="sizeGuideDrawer"
    block-class="size-guide-drawer"
    :class-container="classContainer"
    direction="up-left"
    namespace="sizeGuideDrawer"
    :show-close="!useDenimStyle"
    show-overlay
    type="drawer"
  >
    <template
      v-if="!isDenim"
      #title
    >
      {{ $string('product.form.size_guide' ) }}
    </template>

    <template #body>
      <responsive-image
        v-if="Boolean(filteredSizeGuideData?.diagram)"
        class="size-guide-drawer__image"
        :image="filteredSizeGuideData?.diagram"
      />

      <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
      <img
        v-else-if="sizeGuideData.diagramUrl"
        class="size-guide-drawer__image"
        :src="sizeGuideData.diagramUrl"
        alt=""
      >

      <div
        class="size-guide-drawer__table-container"
        :class="{ 'size-guide-drawer__table-container--alternate-font': !useDenimStyle }"
      >
        <table-snippet
          v-for="(table, tableIndex) of filteredSizeGuideData?.tableArray"
          :key="tableIndex"
          :border-style="useDenimStyle ? 'all' : 'row'"
          :caption="table.showTitle ? table.title : ''"
          :is-first-row-heading="isDenim"
          :table-csv="table.table"
          :use-alternative-font="!useDenimStyle"
          :use-center-text="isCustom"
        />
      </div>

      <div
        v-if="helpContent"
        class="size-guide-drawer__help-container"
      >
        <p
          class="text-heading-m size-guide-drawer__help-title"
          v-text="helpContent.title"
        />

        <p v-text="helpContent.copy" />

        <ul class="size-guide-drawer__help-ctas">
          <li
            v-for="(cta, index) of helpContent.ctas"
            :key="index"
          >
            <button
              v-if="cta.url?.includes('gorgias')"
              class="button button--text size-guide-drawer__cta"
              type="button"
              @click.prevent="handleOpenGorgias"
            >
              <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
              <img
                v-if="cta.icon"
                alt=""
                :src="cta.icon.src"
                role="presentation"
              >

              <span v-text="cta.label" />
            </button>

            <a
              v-else-if="cta.url && cta.label"
              class="text-body-m-regular size-guide-drawer__cta"
              :href="cta.url"
            >
              <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
              <img
                v-if="cta.icon"
                alt=""
                :src="cta.icon.src"
                role="presentation"
              >

              <span v-text="cta.label" />
            </a>
          </li>
        </ul>
      </div>
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Size guide drawer (size-guide-drawer)
 * -----------------------------------------------------------------------------
 * Component to house the body of the size guide drawer.
 *
 * @param {Object} [helpContent] - Content for customer service block.
 * @param {Boolean} [isCustom] - If pulling from product metafields.
 * @param {Boolean} [isDenim] - If the product is Denim and should render styles
 * associated with the denim designs.
 * @param {Object|Array} [sizeGuideData] - Size guide data to render.
 */

import TableSnippet from '~async/table-snippet/table-snippet'
import ResponsiveImage from '~global/images/responsive-image'
import Overlay from '~global/overlay/overlay'

export default {
  name: 'SizeGuideDrawer',

  components: {
    Overlay,
    ResponsiveImage,
    TableSnippet,
  },

  props: {
    helpContent: {
      type: Object,
      default: () => ({
        copy: '',
        ctas: [],
        title: '',
      }),
    },
    isCustom: {
      type: Boolean,
      default: false,
    },
    isDenim: {
      type: Boolean,
      default: false,
    },
    sizeGuideData: {
      type: [Object, Array],
      default: () => [],
      required: true,
    },
  },

  emits: {
    'update-expanded': (payload) => typeof payload === 'boolean',
  },

  data() {
    return {
      filteredSizeGuideData: {},
    }
  },

  computed: {

    /**
     * Return modifier class for class container based on if product is denim.
     * @returns {String}
     */
    classContainer() {
      return this.useDenimStyle ? 'size-guide-drawer--denim' : 'size-guide-drawer'
    },

    /**
     * Return whether table styling should match default or denim styles.
     * @returns {Boolean}
     */
    useDenimStyle() {
      return this.isDenim && !this.isCustom
    },
  },

  mounted() {
    this.setSizeGuide()
    this.$refs.sizeGuideDrawer.addCallback(this.overlayCallback)
  },

  methods: {

    /**
     * Functions that run when the overlay is closed.
     */
    overlayCallback() {
      this.$emit('update-expanded', false)
    },

    /**
     * Return size guide drawer object to render in the size guide, or denim
     * drawer.
     * @returns {Object}
     */
    setSizeGuide() {

      /**
       * Use length to determine if type is Object or Array.
       */
      if (this.isCustom) {
        this.querySizeGuide(this.sizeGuideData.tableFile).then((response) => {
          this.filteredSizeGuideData = this.buildSizeGuideData(response)
        })

        return
      }

      let filteredSizeGuideArray = this.sizeGuideData

      if (this.isDenim && this.filterDenimSizeGuide().length) {
        filteredSizeGuideArray = this.filterDenimSizeGuide()
      }

      const priorityIndex = this.getPriorityIndex(filteredSizeGuideArray)
      this.filteredSizeGuideData = filteredSizeGuideArray[priorityIndex]
    },

    /**
     * Returns formatted size guide data.
     * @param {String} tableCsv - CSV to render.
     * @returns {Object}
     */
    buildSizeGuideData(tableCsv) {
      return {
        diagramUrl: this.sizeGuideData.diagramUrl,
        tableArray: [
          {
            showTitle: true,
            table: tableCsv,
            title: this.sizeGuideData.tableTitle,
          },
        ],
      }
    },

    /**
     * Returns filtered size guide data based on if the collection handle
     * indicates it's for denim products.
     * @returns {Array}
     */
    filterDenimSizeGuide() {
      return this.sizeGuideData.filter((sizeGuide) => {
        return sizeGuide.collectionHandle.includes('denim') ||
          sizeGuide.collectionHandle.includes('jeans')
      })
    },

    /**
     * Loop through size guide tables to find the largest priority number, and
     * return its index.
     * @param {Array} filteredArray - Size guide array after denim filtering.
     * @returns {Number}
     */
    getPriorityIndex(filteredArray) {
      const priorityArray = filteredArray.map((sizeGuide) => {
        return sizeGuide.priority
      })

      const priority = Math.max(...priorityArray)
      return priorityArray.indexOf(priority)
    },

    /**
     * Query Size Guide Data.
     * @param {String} file - File URL.
     * @returns {Promise<String>}
     */
    // eslint-disable-next-line consistent-return
    async querySizeGuide(file) {
      try {
        const res = await fetch(file)
        return res.text()
      } catch (error) {
        cnvs.ReportError('Failed to query size guide data', error.response)
      }
    },

    /**
     * Open Gorgias chat window.
     */
    handleOpenGorgias() {
      window.GorgiasChat.open()
    },
  },
}
</script>

<style lang="scss">
@import './size-guide-drawer';
</style>
